import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Typography, Tag, Badge, Tooltip } from 'antd';
import {
  SecurityScanOutlined,
  DesktopOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  FireOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { DataCard, StatCard } from '../common';
import { useTenant } from '../../context/TenantContext';
import {
  useSophosIdFromCipp,
  useSophosEndpoints,
  useSophosHealth,
  useSophosFirewalls,
  useSophosMdrStatus,
} from '../../hooks';

const { Text } = Typography;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const severityColor = (severity) => {
  switch (severity) {
    case 'high': return 'var(--odin-error)';
    case 'medium': return 'var(--odin-warning)';
    case 'low': return 'var(--odin-success)';
    default: return 'var(--odin-accent)';
  }
};

const HealthIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HealthDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'good': return theme.colors.success;
      case 'suspicious': return theme.colors.warning;
      case 'bad': return theme.colors.error;
      default: return theme.colors.textMuted;
    }
  }};
`;

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-status-awaiting-border);
  border-radius: 4px;
  color: var(--odin-status-awaiting-text);
  font-size: 12px;
  margin-bottom: 12px;
`;

const SectionTitle = styled(Text)`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
  margin-top: 8px;
`;

const FirewallList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FirewallItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text};
`;

const FirewallName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
`;

const FirewallStatus = styled.span`
  color: ${({ $online, theme }) => ($online ? theme.colors.success : theme.colors.error)};
  font-size: 11px;
`;

const FeatureIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const FeatureIcon = styled.span`
  font-size: 14px;
  color: ${({ $enabled, theme }) => ($enabled ? theme.colors.success : theme.colors.warning)};
`;

const FeatureText = styled.span`
  font-size: 12px;
  color: ${({ $enabled, theme }) => ($enabled ? theme.colors.text : theme.colors.warning)};
`;

const UpsellTag = styled(Tag)`
  && {
    font-size: 10px;
    margin-left: 8px;
  }
`;

const HaRoleBadge = styled.span`
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 3px;
  margin-right: 4px;
  background: ${props =>
    props.$role === 'primary' ? 'var(--odin-status-completed-bg)' :
    props.$role === 'auxiliary' ? 'var(--odin-status-active-bg)' : 'var(--odin-status-awaiting-bg)'};
  color: ${props =>
    props.$role === 'primary' ? 'var(--odin-status-completed-text)' :
    props.$role === 'auxiliary' ? 'var(--odin-status-active-text)' : 'var(--odin-status-awaiting-text)'};
`;

const HaSection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
`;

const Divider = styled.div`
  height: 1px;
  background: var(--odin-surface-hover);
  margin: 8px 0;
`;

/**
 * Helper function to parse MCP response content
 */
const parseMCPResponse = (response) => {
  if (!response) return null;

  if (response.content && Array.isArray(response.content)) {
    const textContent = response.content.find(c => c.type === 'text');
    if (textContent?.text) {
      try {
        return JSON.parse(textContent.text);
      } catch (e) {
        console.error('[SecurityWidget] Failed to parse MCP response:', e);
        return null;
      }
    }
  }

  return response;
};


/**
 * SecurityWidget - Displays Sophos endpoint security data with sales upsell info
 *
 * This component integrates with Sophos-MCP to display:
 * - Endpoint count and health status
 * - Active alerts and threats
 * - Protection rate
 * - Firewall status with model names (for sales)
 * - MDR license status with upsell highlighting (for sales)
 *
 * Uses customerMappingService (via useSophosIdFromCipp) to resolve
 * CIPP tenant IDs to Sophos tenant IDs.
 *
 * @param {Object} props
 * @param {string} props.tenantId - Optional direct Sophos tenant ID (overrides mapping)
 * @param {Object} props.data - Legacy data prop for backward compatibility
 * @param {boolean} props.loading - Loading state (legacy)
 * @param {Object} props.error - Error object (legacy)
 * @param {Function} props.onRefresh - Refresh callback
 * @param {boolean} props.compact - Show compact StatCard view
 */
const SecurityWidget = ({
  tenantId: propTenantId = null,
  data: legacyData = null,
  loading: legacyLoading = false,
  error: legacyError = null,
  onRefresh,
  compact = false,
}) => {
  // Get selected tenant from context (CIPP tenant ID)
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Sophos tenant ID using customer mapping service
  const {
    sophosId: mappedSophosId,
    isResolved,
    isLoading: isMappingLoading,
    customer,
  } = useSophosIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId && !propTenantId,
  });

  // Use prop tenantId if provided, otherwise use mapped Sophos ID
  const sophosId = propTenantId || mappedSophosId;
  const hasSophosMapping = !!propTenantId || isResolved;

  // Fetch Sophos endpoints data
  const {
    data: endpointsData,
    isLoading: endpointsLoading,
    error: endpointsError,
    refetch: refetchEndpoints,
  } = useSophosEndpoints(sophosId, {
    enabled: !!sophosId,
  });

  // Fetch Sophos health data
  const {
    data: healthData,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useSophosHealth(sophosId, {
    enabled: !!sophosId,
  });

  // Fetch Sophos firewall data
  const firewallsResult = useSophosFirewalls(sophosId, {
    enabled: !!sophosId,
  });
  const firewallsRaw = firewallsResult?.data;
  const firewallsLoading = firewallsResult?.isLoading ?? false;
  const refetchFirewalls = firewallsResult?.refetch;

  // Fetch Sophos MDR status data
  const mdrResult = useSophosMdrStatus(sophosId, {
    enabled: !!sophosId,
  });
  const mdrRaw = mdrResult?.data;
  const mdrLoading = mdrResult?.isLoading ?? false;
  const refetchMdr = mdrResult?.refetch;

  // Parse firewall and MDR data
  const firewallsParsed = useMemo(() => parseMCPResponse(firewallsRaw), [firewallsRaw]);
  const mdrParsed = useMemo(() => parseMCPResponse(mdrRaw), [mdrRaw]);

  // Process firewall data
  const firewallData = useMemo(() => {
    if (!firewallsParsed?.success && !firewallsParsed?.firewalls) return null;

    const firewalls = firewallsParsed?.firewalls || firewallsParsed?.data?.firewalls || [];
    if (!Array.isArray(firewalls) || firewalls.length === 0) return null;

    // Helper to safely check status (could be string, object, or other type)
    const isOnline = (fw) => {
      // Check if status is an object with connected property (Sophos API format)
      if (typeof fw?.status === 'object' && fw?.status !== null) {
        return fw.status.connected === true && !fw.status.suspended;
      }
      // Handle string status
      const status = typeof fw?.status === 'string' ? fw.status.toLowerCase() : '';
      const connStatus = typeof fw?.connectionStatus === 'string' ? fw.connectionStatus.toLowerCase() : '';
      return status === 'online' || connStatus === 'connected';
    };

    const onlineCount = firewalls.filter(isOnline).length;

    return {
      total: firewalls.length,
      online: onlineCount,
      firewalls: firewalls.map(fw => ({
        name: fw.hostname || fw.name || 'Unknown',
        model: fw.model || fw.productType || 'Firewall',
        online: isOnline(fw),
        haRole: fw.cluster?.status || 'standalone',
        haMode: fw.cluster?.mode || null,
      })),
      haStats: firewallsParsed?.data?.summary?.haStats || null,
    };
  }, [firewallsParsed]);

  // Process MDR status data from get_mdr_status tool
  const mdrStatus = useMemo(() => {
    const data = mdrParsed?.data;
    if (!data) return null;

    return {
      enabled: data.hasMdr === true,
      tier: data.tier, // 'Complete' | 'Essentials' | null
      active: data.active,
    };
  }, [mdrParsed]);

  // Calculate metrics from Sophos data
  const metrics = useMemo(() => {
    // If legacy data is provided, use it for backward compatibility
    if (legacyData && Object.keys(legacyData).length > 0) {
      return {
        endpointCount: legacyData.endpointCount ?? null,
        protectedCount: legacyData.protectedCount ?? null,
        alertCount: legacyData.alertCount ?? null,
        threatCount: legacyData.threatCount ?? null,
        healthyCount: legacyData.healthyCount ?? null,
        healthStatus: null,
      };
    }

    // Extract summary from Sophos get_customer_endpoint_inventory response
    // Response format: { success, data: { customer, summary: { total, workstations, servers } } }
    const summary = endpointsData?.data?.summary || endpointsData?.summary || {};
    const workstations = summary.workstations || {};
    const servers = summary.servers || {};

    // Calculate metrics from summary data
    const endpointCount = summary.total ||
      ((workstations.count || 0) + (servers.count || 0)) || null;

    // Count healthy endpoints (workstations + servers)
    const healthyCount = (workstations.healthy || 0) + (servers.healthy || 0);

    // Count protected (healthy + suspicious - not bad)
    const suspiciousCount = (workstations.suspicious || 0) + (servers.suspicious || 0);
    const protectedCount = healthyCount + suspiciousCount;

    // Count endpoints with bad health
    const badHealthCount = (workstations.bad || 0) + (servers.bad || 0);

    // Extract alerts from health data (get_customer_endpoint_health response)
    // Response format: { success, data: { threats, healthStatus, ... } }
    const healthInfo = healthData?.data || healthData || {};
    const threats = healthInfo.threats || {};
    const threatCount = (threats.atRisk || 0) + (threats.compromised || 0);
    const alertCount = threats.atRisk || badHealthCount || 0;

    // Determine overall health status
    let healthStatus = healthInfo.healthStatus || 'good';
    if (badHealthCount > 0 || threatCount > 0) {
      healthStatus = 'bad';
    } else if (suspiciousCount > 0 || alertCount > 0) {
      healthStatus = 'suspicious';
    }

    return {
      endpointCount: endpointsData !== undefined ? endpointCount : null,
      protectedCount: endpointsData !== undefined ? (protectedCount || endpointCount || 0) : null,
      alertCount: alertCount || 0,
      threatCount: threatCount || 0,
      healthyCount: healthyCount || 0,
      healthStatus,
    };
  }, [legacyData, endpointsData, healthData]);

  // Calculate protection rate
  const protectionRate = metrics.endpointCount && metrics.protectedCount
    ? Math.round((metrics.protectedCount / metrics.endpointCount) * 100)
    : null;

  // Determine loading state
  const isLoading = legacyLoading || isMappingLoading || endpointsLoading || healthLoading || firewallsLoading || mdrLoading;

  // Determine error state
  const error = legacyError || endpointsError || healthError;

  // Refresh handler
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
    if (sophosId) {
      refetchEndpoints?.();
      refetchHealth?.();
      refetchFirewalls?.();
      refetchMdr?.();
    }
  }, [onRefresh, sophosId, refetchEndpoints, refetchHealth, refetchFirewalls, refetchMdr]);

  // Show mapping status warning if no Sophos mapping found
  const showMappingWarning = cippTenantId && !hasSophosMapping && !isMappingLoading && !propTenantId;

  // Compact view (StatCard)
  if (compact) {
    return (
      <StatCard
        icon={SecurityScanOutlined}
        value={metrics.endpointCount}
        label="Protected Endpoints"
        loading={isLoading}
        tooltip={
          hasSophosMapping
            ? `Endpoints protected by Sophos${customer ? ` (${customer.displayName})` : ''}`
            : 'Configure Sophos mapping in Odin-MCP to view data'
        }
      />
    );
  }

  // Full widget view
  return (
    <DataCard
      title="Sophos"
      icon={SecurityScanOutlined}
      loading={isLoading}
      error={error}
      onRefresh={handleRefresh}
      empty={!metrics.endpointCount && !firewallData && !mdrStatus && !showMappingWarning}
      extra={
        metrics.healthStatus && (
          <Tooltip title={`Overall health: ${metrics.healthStatus}`}>
            <HealthIndicator>
              <HealthDot $status={metrics.healthStatus} />
              <Text style={{ fontSize: 12, color: 'var(--odin-text-secondary)' }}>
                {metrics.healthStatus === 'good' ? 'Healthy' :
                 metrics.healthStatus === 'suspicious' ? 'Warning' : 'Critical'}
              </Text>
            </HealthIndicator>
          </Tooltip>
        )
      }
    >
      {showMappingWarning && (
        <MappingWarning>
          <LinkOutlined />
          <span>
            No Sophos mapping found for this tenant. Add mapping in Odin-MCP config.
          </span>
        </MappingWarning>
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <DesktopOutlined />
              Endpoints
            </MetricLabel>
            <MetricValue>{metrics.endpointCount ?? '--'}</MetricValue>
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <CheckCircleOutlined />
              Healthy
            </MetricLabel>
            <MetricValue>
              {metrics.healthyCount ?? '--'}
              {protectionRate !== null && (
                <Tag
                  color={protectionRate >= 95 ? 'green' : protectionRate >= 80 ? 'orange' : 'red'}
                  style={{ marginLeft: 8 }}
                >
                  {protectionRate}%
                </Tag>
              )}
            </MetricValue>
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <WarningOutlined />
              Active Alerts
            </MetricLabel>
            <Badge
              count={metrics.alertCount ?? 0}
              color={severityColor(metrics.alertCount > 5 ? 'high' : metrics.alertCount > 0 ? 'medium' : 'low')}
              showZero
            />
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <ExclamationCircleOutlined />
              Threats (7d)
            </MetricLabel>
            <Badge
              count={metrics.threatCount ?? 0}
              color={severityColor(metrics.threatCount > 10 ? 'high' : metrics.threatCount > 0 ? 'medium' : 'low')}
              showZero
            />
          </MetricRow>
        </Col>
      </Row>

      {/* Firewalls Section */}
      {firewallData && (
        <>
          <Divider />
          <SectionTitle>Firewalls</SectionTitle>
          <MetricRow>
            <MetricLabel>
              <FireOutlined />
              Status
            </MetricLabel>
            <MetricValue>
              <Tag color={firewallData?.online === firewallData?.total ? 'green' : 'orange'}>
                {firewallData?.online || 0}/{firewallData?.total || 0} online
              </Tag>
            </MetricValue>
          </MetricRow>
          <FirewallList>
            {firewallData?.firewalls?.map((fw, index) => (
              <Tooltip key={index} title={`${fw?.model || 'Firewall'} - ${fw?.online ? 'Online' : 'Offline'}${fw?.haRole && fw.haRole !== 'standalone' ? ` (HA ${fw.haRole})` : ''}`}>
                <FirewallItem>
                  <FirewallName>{fw?.model || 'Firewall'}</FirewallName>
                  {fw?.haRole && fw.haRole !== 'standalone' && (
                    <HaRoleBadge $role={fw.haRole}>
                      {fw.haRole === 'primary' ? 'Pri' : fw.haRole === 'auxiliary' ? 'Aux' : '⚠️'}
                    </HaRoleBadge>
                  )}
                  <FirewallStatus $online={fw?.online}>
                    {fw?.online ? '● Online' : '○ Offline'}
                  </FirewallStatus>
                </FirewallItem>
              </Tooltip>
            ))}
          </FirewallList>

          {/* HA Configuration Summary */}
          {firewallData?.haStats?.totalHAPairs > 0 && (
            <HaSection>
              <MetricRow>
                <MetricLabel><SafetyCertificateOutlined /> HA Pairs</MetricLabel>
                <MetricValue>
                  <Tag color="blue">{firewallData.haStats.totalHAPairs} configured</Tag>
                </MetricValue>
              </MetricRow>
              {firewallData.haStats.faultConditions > 0 && (
                <MetricRow>
                  <MetricLabel style={{ color: 'var(--odin-error)' }}>
                    <WarningOutlined /> HA Faults
                  </MetricLabel>
                  <MetricValue><Tag color="red">{firewallData.haStats.faultConditions}</Tag></MetricValue>
                </MetricRow>
              )}
            </HaSection>
          )}
        </>
      )}

      {/* MDR License Section */}
      {mdrStatus !== null && (
        <>
          <Divider />
          <SectionTitle>Advanced Protection</SectionTitle>
          <FeatureIndicator>
            <FeatureIcon $enabled={mdrStatus?.enabled}>
              {mdrStatus?.enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            </FeatureIcon>
            <SafetyCertificateOutlined style={{ color: mdrStatus?.enabled ? 'var(--odin-accent)' : 'var(--odin-warning)' }} />
            <FeatureText $enabled={mdrStatus?.enabled}>
              MDR {mdrStatus?.tier ? `(${mdrStatus.tier})` : 'License'} {mdrStatus?.enabled ? '(Active)' : ''}
            </FeatureText>
            {!mdrStatus?.enabled && <UpsellTag color="warning">Upsell</UpsellTag>}
          </FeatureIndicator>
        </>
      )}
    </DataCard>
  );
};

SecurityWidget.propTypes = {
  /** Direct Sophos tenant ID (bypasses customer mapping) */
  tenantId: PropTypes.string,
  /** Legacy data prop for backward compatibility */
  data: PropTypes.shape({
    endpointCount: PropTypes.number,
    protectedCount: PropTypes.number,
    alertCount: PropTypes.number,
    threatCount: PropTypes.number,
    healthyCount: PropTypes.number,
  }),
  /** Legacy loading state */
  loading: PropTypes.bool,
  /** Legacy error object */
  error: PropTypes.object,
  /** Callback when refresh is requested */
  onRefresh: PropTypes.func,
  /** Show compact StatCard view */
  compact: PropTypes.bool,
};

export default SecurityWidget;
